import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home, ArrowLeftRight, Wallet, Receipt, CreditCard, Users,
  Percent, Gift, Banknote, Share2, History,
  Package, Settings, SlidersHorizontal, Code, Blocks,
  LogOut, ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useThemeMode } from "@/theme/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import LogoutDialog from "@/components/common/LogoutDialog";

export const SIDEBAR_WIDTH = 260;

interface NavChild {
  label: string;
  path: string;
  icon: React.ReactNode;
  external?: boolean;
}

interface NavGroup {
  label: string;
  icon: React.ReactNode;
  children: NavChild[];
}

interface NavSingle {
  label: string;
  icon: React.ReactNode;
  path: string;
}

type NavItem = NavSingle | NavGroup;

function isGroup(item: NavItem): item is NavGroup {
  return "children" in item;
}

const iconClass = "h-[18px] w-[18px] shrink-0";
const childIconClass = "h-4 w-4 shrink-0";

const navItems: NavItem[] = [
  { label: "Home", icon: <Home className={iconClass} />, path: "/" },
  {
    label: "Transactions",
    icon: <ArrowLeftRight className={iconClass} />,
    children: [
      { label: "Wallet", path: "/transactions/wallet", icon: <Wallet className={childIconClass} /> },
      { label: "Invoices", path: "/transactions/invoices", icon: <Receipt className={childIconClass} /> },
      { label: "Credit", path: "/transactions/credit", icon: <CreditCard className={childIconClass} /> },
      { label: "Customers", path: "/transactions/customers", icon: <Users className={childIconClass} /> },
    ],
  },
  {
    label: "Discount Mapping",
    icon: <Percent className={iconClass} />,
    children: [
      { label: "Gift Cards", path: "/discount-mapping/gift-cards", icon: <Gift className={childIconClass} /> },
      { label: "BBPS", path: "/discount-mapping/bbps", icon: <Banknote className={childIconClass} /> },
      { label: "Shared Subscriptions", path: "/discount-mapping/shared-subscriptions", icon: <Share2 className={childIconClass} /> },
    ],
  },
  {
    label: "Order History",
    icon: <History className={iconClass} />,
    children: [
      { label: "Gift Cards", path: "/order-history/gift-cards", icon: <Gift className={childIconClass} /> },
      { label: "BBPS", path: "/order-history/bbps", icon: <Banknote className={childIconClass} /> },
      { label: "Shared Subscriptions", path: "/order-history/shared-subscriptions", icon: <Share2 className={childIconClass} /> },
    ],
  },
  {
    label: "Stocks",
    icon: <Package className={iconClass} />,
    children: [
      { label: "Gift Cards", path: "/stocks/gift-cards", icon: <Gift className={childIconClass} /> },
    ],
  },
  {
    label: "Settings",
    icon: <Settings className={iconClass} />,
    children: [
      { label: "General", path: "/settings/general", icon: <SlidersHorizontal className={childIconClass} /> },
      { label: "Developer", path: "https://docs.pocoai.store/?login=old", icon: <Code className={childIconClass} />, external: true },
      { label: "SDK Configuration", path: "/settings/sdk", icon: <Blocks className={childIconClass} /> },
    ],
  },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant: "permanent" | "temporary";
}

export default function Sidebar({ open, onClose, variant }: SidebarProps) {
  const { mode } = useThemeMode();
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const getInitialOpen = () => {
    const openGroups: Record<string, boolean> = {};
    navItems.forEach((item) => {
      if (isGroup(item)) {
        openGroups[item.label] = item.children.some(
          (child) => location.pathname === child.path
        );
      }
    });
    return openGroups;
  };

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(getInitialOpen);

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleNav = (path: string) => {
    navigate(path);
    if (variant === "temporary") onClose();
  };

  const isActive = (path: string) => location.pathname === path;

  const sidebarContent = (
    <div className="flex flex-col h-full bg-card">
      {/* Logo */}
      <div className="flex items-center justify-center h-16 border-b border-border">
        <img
          src={mode === "light" ? "/pocoai-black.png" : "/pocoai-white.png"}
          alt="Pocoai"
          className="h-7 w-auto object-contain"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="space-y-1">
          {navItems.map((item) => {
            if (!isGroup(item)) {
              return (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  className={cn(
                    "flex items-center gap-3 w-full rounded-lg px-3 py-2 text-[13px] font-medium transition-colors cursor-pointer",
                    isActive(item.path)
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  {item.icon}
                  {item.label}
                </button>
              );
            }

            const groupActive = item.children.some((c) => isActive(c.path));

            return (
              <Collapsible
                key={item.label}
                open={openGroups[item.label]}
                onOpenChange={() => toggleGroup(item.label)}
              >
                <CollapsibleTrigger
                  render={
                    <button
                      className={cn(
                        "flex items-center gap-3 w-full rounded-lg px-3 py-2 text-[13px] font-medium transition-colors cursor-pointer",
                        groupActive && !openGroups[item.label]
                          ? "bg-accent text-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      )}
                    />
                  }
                >
                  {item.icon}
                  <span className="flex-1 text-left">{item.label}</span>
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                      openGroups[item.label] && "rotate-180"
                    )}
                  />
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="ml-5 pl-3 py-1 border-l border-border space-y-0.5">
                    {item.children.map((child) =>
                      child.external ? (
                        <a
                          key={child.path}
                          href={child.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            "flex items-center gap-2.5 w-full rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors cursor-pointer",
                            "text-muted-foreground hover:bg-accent hover:text-foreground"
                          )}
                        >
                          {child.icon}
                          {child.label}
                        </a>
                      ) : (
                        <button
                          key={child.path}
                          onClick={() => handleNav(child.path)}
                          className={cn(
                            "flex items-center gap-2.5 w-full rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors cursor-pointer",
                            isActive(child.path)
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:bg-accent hover:text-foreground"
                          )}
                        >
                          {child.icon}
                          {child.label}
                        </button>
                      )
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      </nav>

      {/* Logout */}
      <div className="px-3 py-3 border-t border-border">
        <button
          onClick={() => setLogoutOpen(true)}
          className="flex items-center justify-center gap-2 w-full rounded-lg px-3 py-2 text-[13px] font-medium text-muted-foreground hover:bg-red-600 hover:text-white transition-colors cursor-pointer"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>
      <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} onConfirm={() => { setLogoutOpen(false); logout(); }} />
    </div>
  );

  if (variant === "temporary") {
    return (
      <Sheet open={open} onOpenChange={(val) => { if (!val) onClose(); }}>
        <SheetContent side="left" showCloseButton={false} className="p-0" style={{ width: SIDEBAR_WIDTH }}>
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      className="fixed inset-y-0 left-0 z-40 border-r border-border bg-card"
      style={{ width: SIDEBAR_WIDTH }}
    >
      {sidebarContent}
    </aside>
  );
}
